import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { IconBadge } from '../components/icons/ServiceIcons';
import './ServicePage.css';
const generativeAIArtwork = '/assets/illustrations/generative-ai-lab.svg';

const GenerativeAI = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [processRef, processInView] = useInView({ threshold: 0.2, triggerOnce: true });

  const offerings = [
    {
      icon: 'assessment',
      title: 'Use-Case Discovery',
      description: 'Innovation workshops, value framing, and risk assessment to prioritise pilots that deliver measurable outcomes.'
    },
    {
      icon: 'brain',
      title: 'Model Strategy & Architecture',
      description: 'Model selection, prompt engineering, and retrieval augmentation aligned to data privacy and compliance mandates.'
    },
    {
      icon: 'beaker',
      title: 'Rapid Prototyping',
      description: 'Design sprints and experimentation sandboxes that validate UX, accuracy, and safety guardrails before scaling.'
    },
    {
      icon: 'rocket',
      title: 'Production Deployment',
      description: 'CI/CD for LLMs, monitoring, and automation of model refresh, feedback loops, and human-in-the-loop oversight.'
    },
    {
      icon: 'governance',
      title: 'Responsible AI Governance',
      description: 'Policy frameworks, audit logs, risk scoring, and ethical guidelines embedded throughout the solution lifecycle.'
    },
    {
      icon: 'people',
      title: 'Adoption & Change Enablement',
      description: 'Training, change management, and success metrics that drive user adoption and continuous optimisation.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discover',
      description: 'Assess data, workflows, and stakeholder readiness to prioritise the highest-value generative AI scenarios.',
      icon: 'assessment'
    },
    {
      step: '02',
      title: 'Design',
      description: 'Shape solution architecture, guardrails, and experiment backlog with a clear definition of success.',
      icon: 'architecture'
    },
    {
      step: '03',
      title: 'Build',
      description: 'Develop prototypes, integrate with systems, and validate outcomes with iterative testing and evaluation.',
      icon: 'toolkit'
    },
    {
      step: '04',
      title: 'Launch',
      description: 'Ship production-ready experiences with monitoring, human review processes, and performance baselines.',
      icon: 'rocket'
    },
    {
      step: '05',
      title: 'Scale',
      description: 'Expand to enterprise rollout with adoption programmes, optimisation, and continuous compliance checks.',
      icon: 'analytics'
    }
  ];

  const programmes = [
    {
      title: 'Knowledge Assistants',
      description: 'Unified conversational interfaces for employees or customers powered by curated knowledge bases and retrieval pipelines.',
      icon: 'chat'
    },
    {
      title: 'Content Generation Pipelines',
      description: 'Brand-safe creative tooling for marketing and product teams with approval workflows and performance analytics.',
      icon: 'spark'
    },
    {
      title: 'Workflow Copilots',
      description: 'Embedded assistants inside productivity suites that orchestrate multi-step tasks and automate decisions.',
      icon: 'automation'
    },
    {
      title: 'Document Intelligence Automation',
      description: 'Classification, extraction, and triage of complex documents with human-in-the-loop review and compliance logging.',
      icon: 'document'
    }
  ];

  const technologies = [
    {
      name: 'OpenAI & Azure OpenAI',
      tagline: 'Foundation Models',
      description: 'GPT-4, Vision, and fine-tuning services wrapped with Azure compliance guardrails.',
      icon: 'brain'
    },
    {
      name: 'Anthropic Claude',
      tagline: 'Responsible AI',
      description: 'Constitutional AI and long-context reasoning for regulated industries.',
      icon: 'brain'
    },
    {
      name: 'Amazon Bedrock',
      tagline: 'Managed FM Platform',
      description: 'Host foundational models with AWS security, private VPC endpoints, and observability.',
      icon: 'cloud'
    },
    {
      name: 'Google Vertex AI',
      tagline: 'Generative AI Studio',
      description: 'Model Garden, PaLM, and pipelines integrated with BigQuery, Looker, and MLOps.',
      icon: 'cloud'
    },
    {
      name: 'LangChain & Semantic Kernel',
      tagline: 'Agent Orchestration',
      description: 'Toolkit orchestration, memory management, and guardrails for production workflows.',
      icon: 'integration'
    },
    {
      name: 'LlamaIndex & Haystack',
      tagline: 'Retrieval Middleware',
      description: 'Composable retrieval augmented generation with caching and evaluation hooks.',
      icon: 'layers'
    },
    {
      name: 'Pinecone & Weaviate',
      tagline: 'Vector Stores',
      description: 'Low-latency semantic search with filtering, hybrid scoring, and managed scaling.',
      icon: 'dataStack'
    },
    {
      name: 'Redis & Milvus',
      tagline: 'On-Prem Vector Options',
      description: 'Latency-optimised persistence for embeddings across edge and hybrid environments.',
      icon: 'dataStack'
    },
    {
      name: 'Azure Cognitive Search & AWS Kendra',
      tagline: 'Enterprise Retrieval',
      description: 'Secure indexing, ACL enforcement, and connectors to unify institutional knowledge.',
      icon: 'search'
    },
    {
      name: 'PromptLayer & W&B',
      tagline: 'PromptOps & Monitoring',
      description: 'Prompt versioning, evaluation datasets, and telemetry for ongoing optimisation.',
      icon: 'insight'
    }
  ];

  const marqueeRows = [
    technologies.filter((_, index) => index % 2 === 0),
    technologies.filter((_, index) => index % 2 === 1)
  ].filter((row) => row.length);

  return (
    <motion.div 
      className="service-page generative-ai-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      <section className="service-hero parallax-container" ref={heroRef}>
        <div className="parallax-bg"></div>
        <div className="container">
          <motion.div 
            className="hero-content text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div 
              className="hero-icon"
              initial={{ scale: 0 }}
              animate={heroInView ? { scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <img 
                src={generativeAIArtwork} 
                alt="Abstract generative AI illustration" 
              />
            </motion.div>
            <h1>Generative AI</h1>
            <p className="hero-description">
              Harness generative AI responsibly with discovery, experimentation, and production-scale deployments guided by CloudRaize experts. We deliver copilots, knowledge assistants, and intelligent automation solutions shaped to your governance and brand standards.
            </p>
            <Link to="/contact" className="btn">Explore AI Solutions</Link>
          </motion.div>
        </div>
      </section>

      <section className="what-we-do section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>What We Do</h2>
            <p>
              We align strategy, engineering, and change management so generative AI delivers sustainable value. From safeguarding data to embedding oversight, our teams co-create with your stakeholders every step of the way.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="features-section section section-alt" ref={featuresRef}>
        <div className="container">
          <div className="grid-3">
            {offerings.map((item, index) => (
              <motion.div
                key={item.title}
                className="feature-card card"
                initial={{ opacity: 0, y: 50 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="feature-icon">
                  <IconBadge name={item.icon} size={72} backgroundOpacity={0.24} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="process-section section" ref={processRef}>
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={processInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Generative AI Journey</h2>
            <p>Confident experimentation with governance, scale, and adoption built in</p>
          </motion.div>

          <div className="process-timeline">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                className="process-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={processInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="process-number">{item.step}</div>
                <div className="process-content">
                  <div className="process-icon">
                    <IconBadge
                      name={item.icon}
                      className="icon-badge--sm"
                      size={58}
                      backgroundOpacity={0.24}
                    />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="use-cases-section section section-alt">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Signature Programmes</h2>
            <p>Flagship engagements our team delivers from discovery through production</p>
          </motion.div>

          <div className="grid-4">
            {programmes.map((programme, index) => (
              <motion.div
                key={programme.title}
                className="use-case-item card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                viewport={{ once: true }}
              >
                <div className="use-case-icon">
                  <IconBadge
                    name={programme.icon}
                    className="icon-badge--sm"
                    size={56}
                    backgroundOpacity={0.24}
                  />
                </div>
                <h3>{programme.title}</h3>
                <p>{programme.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="technologies-section section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Platforms & Tooling</h2>
            <p>We work with leading models, vector stores, and orchestration libraries</p>
          </motion.div>

          <motion.div
            className="tech-marquee-wrapper"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {marqueeRows.map((row, rowIndex) => (
              <div
                className={`tech-marquee${rowIndex % 2 === 1 ? ' tech-marquee--reverse' : ''}`}
                key={`genai-row-${rowIndex}`}
              >
                <div className="tech-marquee__track">
                  {[...row, ...row].map((tech, index) => (
                    <div className="tech-marquee__card" key={`genai-tech-${rowIndex}-${index}-${tech.name}`}>
                      <IconBadge
                        name={tech.icon}
                        className="tech-marquee__badge icon-badge--sm"
                        size={58}
                        backgroundOpacity={0.24}
                      />
                      <div className="tech-marquee__label">
                        <span className="tech-marquee__title">{tech.name}</span>
                        {tech.tagline && (
                          <span className="tech-marquee__subtitle">{tech.tagline}</span>
                        )}
                        {tech.description && (
                          <span className="tech-marquee__description">{tech.description}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default GenerativeAI;
